<script setup lang="ts">
/**
 * A QR code as inline SVG.
 *
 * Inline rather than an `<img>` — the opposite of the rule for sponsor logos,
 * and for the same reason. A sponsor logo is third-party artwork that must not
 * be inlined because it could carry script; this markup is generated here from a
 * URL the API already vetted, so there is nothing to neutralise, and inlining
 * keeps the code sharp at any zone size with no network round trip.
 *
 * The plate is deliberately painted light with dark modules regardless of the
 * zone's theme: a scanner needs the contrast polarity it expects, and a themed
 * QR code that no phone reads is decoration.
 */
import { computed } from 'vue';
import { QR_QUIET_ZONE, qrMatrix, qrSvgPath, qrViewBoxSize } from './qr';

const props = defineProps<{
  value: string;
  /** Shown under the code, and used as the image's accessible name. */
  caption?: string | undefined;
}>();

const matrix = computed(() => qrMatrix(props.value));
const viewBox = computed(() =>
  matrix.value ? `0 0 ${qrViewBoxSize(matrix.value)} ${qrViewBoxSize(matrix.value)}` : '',
);
const path = computed(() => (matrix.value ? qrSvgPath(matrix.value, QR_QUIET_ZONE) : ''));
const side = computed(() => (matrix.value ? qrViewBoxSize(matrix.value) : 0));
</script>

<template>
  <figure v-if="matrix" class="lr42-qr">
    <svg
      class="lr42-qr__code"
      :viewBox="viewBox"
      role="img"
      :aria-label="caption ?? value"
      shape-rendering="crispEdges"
    >
      <rect :width="side" :height="side" fill="var(--lr42-qr-bg, #ffffff)" />
      <path :d="path" fill="var(--lr42-qr-fg, #111111)" />
    </svg>
    <figcaption v-if="caption" class="lr42-qr__caption">{{ caption }}</figcaption>
  </figure>
</template>

<style scoped>
.lr42-qr {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
  min-width: 0;
}
.lr42-qr__code {
  width: var(--lr42-qr-size, clamp(56px, 9vw, 180px));
  height: var(--lr42-qr-size, clamp(56px, 9vw, 180px));
  /* The quiet zone is inside the viewBox, so the plate may sit flush. */
  border-radius: 2px;
  display: block;
}
.lr42-qr__caption {
  font-size: var(--lr42-caption-size, clamp(9px, 1vw, 18px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.7));
  text-align: center;
  white-space: nowrap;
}
</style>
